import React, { useEffect, useRef, useState } from "react";

import { ReactComponent as CopyIcon } from "@common/assets/images/copy-paste-icon.svg";
import * as Styled from "main/pages/Release/views/MetricDetails/shared/EndpointUrl.styles";

interface EndpointUrlProps {
  url: string;
  isError?: boolean;
  compact?: boolean;
}

const isParamSegment = (segment: string): boolean => segment.startsWith(":");

const toPathSegments = (url: string): string[] => {
  const withoutScheme = url.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
  const pathStart = withoutScheme.indexOf("/");

  if (pathStart === -1) return [];

  return withoutScheme.slice(pathStart).replace(/\/+$/, "").split("/").filter(Boolean);
};

export function EndpointUrl({ url, isError, compact }: EndpointUrlProps) {
  const segments = toPathSegments(url);

  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copiedTimeout.current), []);

  const onCopy = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    void navigator.clipboard.writeText(url);

    setCopied(true);
    clearTimeout(copiedTimeout.current);
    copiedTimeout.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Styled.EndpointUrlRoot>
      <Styled.UrlTooltip
        text={url}
        position={"top"}
        portal
        contentStyle={Styled.urlTooltipContentStyle}
      >
        <Styled.EndpointUrl
          p2
          $isError={isError}
          $compact={compact}
        >
          {segments.length === 0 ? (
            url
          ) : (
            <>
              <Styled.Origin $isError={isError}>.../</Styled.Origin>
              {segments.map((segment, index) => (
                <React.Fragment key={`${segment}-${index}`}>
                  {index > 0 && <Styled.Separator $isError={isError}>/</Styled.Separator>}
                  {isParamSegment(segment) ? (
                    <Styled.ParamBadge
                      $isError={isError}
                      $compact={compact}
                      title={segment}
                    />
                  ) : (
                    segment
                  )}
                </React.Fragment>
              ))}
            </>
          )}
        </Styled.EndpointUrl>
      </Styled.UrlTooltip>

      <Styled.CopyAction
        text={copied ? "Copied!" : "Copy"}
        position={"top"}
        portal
      >
        <Styled.CopyButton
          type={"button"}
          aria-label={"Copy endpoint URL"}
          onClick={onCopy}
        >
          <CopyIcon />
        </Styled.CopyButton>
      </Styled.CopyAction>
    </Styled.EndpointUrlRoot>
  );
}
